import { deleteOwnAccount } from "@/server/services/userService";
import { ApiError, AppError } from "@/server/apiError";
import { getWebPushStorageKeys } from "@/components/web-push/webPushUtils";
import { ERROR_REPORT_QUEUE_KEY } from "@/components/error-boundary/queue";
import { useAuthState } from "@/zustand/AuthState";
import { useConnectionState } from "@/zustand/ConnectionState";
import { safeStorage } from "@/utils/safeStorage";
import { isUserAuthenticated, logoutUser } from "./auth";

/**
 * Account deletion v2 (`DELETE /umbra/v2/users/me`) — the middleware side of
 * docs/account-deletion.md. Split out of auth.ts (CLAUDE.md domain-directory
 * rule); logout/login stay in auth.ts, everything deletion-specific lives
 * here.
 */

/**
 * Local teardown for a DELETED account — shared by the v2 delete flow (after
 * a 204) and the `user` Delete socket handler (another device/tab of the
 * deleted account, or a support-panel deletion).
 *
 * Idempotent, MUST NOT throw, and deliberately does more than logoutUser:
 * this device will never see this account again, so decrypted OPFS
 * attachment plaintext, Cache Storage, the per-user web-push registration
 * and the live socket all go too. (logoutUser leaves those for a normal
 * sign-out — the same user usually signs back in.)
 */
export const accountDeletedLocalTeardown = async (options?: {
  /**
   * The deleted account's username, when the CALLER knows it better than the
   * auth store — the `user` Delete socket handler passes the event payload's
   * username because the 401-refresh-failure race can wipe the store before
   * this runs, which would otherwise skip the per-user push-key cleanup.
   */
  username?: string;
}): Promise<void> => {
  const username = options?.username || useAuthState.getState().username;

  // Synchronous store/cache/Dexie teardown. Server logout is skipped — the
  // account no longer exists, the calls would be guaranteed 401/404 noise.
  logoutUser({ skipServerLogout: true });

  // The remaining steps are independent and each best-effort — run them
  // concurrently; this teardown gates the "deleted → redirect to login" UX.
  await Promise.allSettled([
    // The useSocket effect's token-change path early-returns when tk clears,
    // so the deleted account's socket must be torn down explicitly. Lazy
    // import: useSocket's module graph pulls browser-only globals that must
    // not load with this middleware.
    import("@/socketIO/useSocket").then((m) => {
      m.cleanupSocketInstance();
      useConnectionState.getState().resetConnectionState();
    }),
    // Decrypted attachment plaintext in OPFS (files ≥5 MB are stored decrypted).
    import("@/lib/storage/attachmentStorage").then((m) =>
      m.clearAllAttachmentContent(),
    ),
    // Cache Storage API (SW-cached responses for this account). The workbox
    // PRECACHE is kept: it is the app shell (code, not user data), and Cache
    // Storage is device-scoped — deleting it would leave the next user of an
    // installed PWA with a white screen offline until the SW re-precaches.
    (async () => {
      if ("caches" in window) {
        const cacheKeys = await caches.keys();
        await Promise.all(
          cacheKeys
            .filter((key) => !key.startsWith("workbox-precache"))
            .map((key) => caches.delete(key)),
        );
      }
    })(),
    // Browser push: unsubscribe the registration.
    (async () => {
      const registration = await navigator.serviceWorker?.getRegistration();
      await (await registration?.pushManager?.getSubscription())?.unsubscribe();
    })(),
  ]);

  // Drop the web-push storage keys. subscriptionData is device-scoped (a
  // constant key, not username-namespaced) and describes the subscription
  // just unsubscribed above — remove it UNCONDITIONALLY, so a wiped-auth
  // race (no username to resolve) can't leave it to collide with a later
  // session's subscription-restore logic. The other two ARE username-scoped.
  safeStorage.remove(getWebPushStorageKeys("").subscriptionData);
  if (username) {
    const keys = getWebPushStorageKeys(username);
    safeStorage.remove(keys.userPreference);
    safeStorage.remove(keys.activeStatus);
  }

  // Queued error reports persist `{ label, content, username }` bodies and
  // are POSTed on the next app load / `online` event — possibly under a
  // different person's session. A deleted account's reports must not outlive
  // it (2026-08-17 review §4).
  safeStorage.remove(ERROR_REPORT_QUEUE_KEY);
};

/**
 * Account deletion v2 — `DELETE /umbra/v2/users/me`.
 *
 * ORDERING INVARIANT: once the server has deleted the account, NOTHING may
 * surface to the caller as a failure — a post-success teardown hiccup
 * rendered as "could not delete account" would send the user into a retry
 * loop against an account that is already gone. So the DELETE call is the
 * ONLY thing allowed to throw; teardown failures are swallowed (logged by
 * their own layers).
 *
 * 404 Account.NotFound is SUCCESS: the token was valid but the account is
 * already gone — reachable legitimately when the 401 interceptor replays a
 * DELETE whose first attempt succeeded.
 */
export const deleteOwnUserAccount = async (): Promise<void> => {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  // ---- Phase 1: the DELETE. The only phase that may throw. ----
  try {
    await deleteOwnAccount();
  } catch (error) {
    // Branch on the TITLE, never on the bare status (our own contract rule):
    // a misrouted/gateway 404 with a problem-shaped body must not trigger a
    // full local wipe for an account that still exists.
    const alreadyGone =
      error instanceof ApiError &&
      (error.data as AppError | undefined)?.title === "errors.Account.NotFound";
    if (!alreadyGone) {
      throw error;
    }
  }

  // ---- Phase 2: the account is gone. Terminal success from here on. ----
  try {
    await accountDeletedLocalTeardown();
  } catch {
    // Never convert a successful deletion into a displayed failure.
  }
};
